/**
 * Runtime configuration for talking to the game server (M2 dev + M5 prod).
 *
 * Two deployment modes, selected at build time by `VITE_DEPLOY_MODE` (see `env.ts`):
 *
 *   - **dev** (default): the browser talks to the local dev game-server on an
 *     absolute base URL (`VITE_SERVER_URL`, localhost default) — HTTP for the
 *     `/dev/*` ticket/seat endpoints, WS for the live game. `npm run dev` works
 *     with zero config; the Playwright e2e points it at an ephemeral port.
 *   - **prod** (`VITE_DEPLOY_MODE=prod`, M5): the app is served by CloudFront on
 *     the SAME origin as the `/api/*` control plane and the `/ws` socket, so the
 *     HTTP base is empty (relative URLs) and the WS base derives from the page's
 *     own origin at runtime. This keeps the built bundle DNS-agnostic — no ALB or
 *     CloudFront domain is baked into it (M5.4).
 *
 * Real auth + a routed WS endpoint per game arrive with the control plane in M7.
 */

import { readDeployMode, readServerUrl } from './env';
import { getSessionId } from './net/session-id';

/** True when built for the deployed single-origin CloudFront site (M5). */
export const IS_PROD_DEPLOY: boolean = readDeployMode() === 'prod';

/**
 * HTTP base for the control endpoints. In prod this is empty so requests are
 * relative (same-origin behind CloudFront). In dev it is the absolute dev-server
 * URL (`VITE_SERVER_URL`, localhost default).
 */
export const SERVER_HTTP_URL: string = IS_PROD_DEPLOY
  ? ''
  : (readServerUrl() ?? 'http://localhost:4000');

/**
 * Derive the WebSocket base (`ws(s)://…`) from an HTTP base. An empty base (prod,
 * same-origin) resolves against the current page origin at call time — so the
 * socket connects back to whatever host served the app (the CloudFront domain).
 */
export function wsBaseUrl(httpUrl: string = SERVER_HTTP_URL): string {
  if (httpUrl === '') {
    // Same-origin: use the page's origin (https → wss). Guard for non-browser
    // (test) environments where `location` may be absent.
    const origin =
      typeof location !== 'undefined' && location.origin
        ? location.origin
        : 'http://localhost';
    return origin.replace(/^http/, 'ws');
  }
  return httpUrl.replace(/^http/, 'ws');
}

/**
 * Build the full WebSocket URL for a validated ticket. Carries the stable browser-session
 * id as `&sid=` (#35 session work): a browser CANNOT set custom headers on a WebSocket
 * handshake, so the session id rides as a query param — the server reads it at the `/ws`
 * upgrade and stamps `session.id` on every WS-command span for that socket, joining this
 * user's RUM session to their gameplay traces. (The trace context itself is propagated
 * separately via the ALB's `X-Amzn-Trace-Id` on the upgrade request.)
 */
export function wsUrlForTicket(
  ticket: string,
  httpUrl: string = SERVER_HTTP_URL,
): string {
  return `${wsBaseUrl(httpUrl)}/ws?ticket=${encodeURIComponent(ticket)}&sid=${encodeURIComponent(getSessionId())}`;
}
